import {
    PortfolioProperty,
    PortfolioSlot,
} from 'src/redux/user_portfolio/user_portfolio.types';

/** package_slot_category_id que identifica a un slot Prime. El resto = Destacado. */
export const PRIME_CATEGORY_ID = 8;

/** Días dentro de los cuales un slot se considera "por vencer". */
export const EXPIRING_SOON_DAYS = 7;

export type SlotCategory = 'prime' | 'destacado';

/** Estado de visibilidad derivado de una propiedad respecto a sus slots. */
export type PropertyVisibility = 'libre' | 'prime' | 'destacado' | 'asignada';

export const isPrimeSlot = (slot: PortfolioSlot): boolean =>
    slot.package_slot_category_id === PRIME_CATEGORY_ID;

export const slotCategory = (slot: PortfolioSlot): SlotCategory =>
    isPrimeSlot(slot) ? 'prime' : 'destacado';

/** Separa los slots por categoría (Prime vs Destacado). */
export const splitSlotsByCategory = (
    slots: PortfolioSlot[]
): Record<SlotCategory, PortfolioSlot[]> => {
    const prime: PortfolioSlot[] = [];
    const destacado: PortfolioSlot[] = [];
    slots.forEach((slot) => {
        if (isPrimeSlot(slot)) {
            prime.push(slot);
        } else {
            destacado.push(slot);
        }
    });
    return { prime, destacado };
};

export const isOccupiedSlot = (slot: PortfolioSlot): boolean => slot.property_id != null;

/** Separa los slots de una categoría en ocupados y disponibles. */
export const partitionSlots = (
    slots: PortfolioSlot[]
): { occupied: PortfolioSlot[]; available: PortfolioSlot[] } => {
    const occupied: PortfolioSlot[] = [];
    const available: PortfolioSlot[] = [];
    slots.forEach((slot) => {
        if (isOccupiedSlot(slot)) {
            occupied.push(slot);
        } else {
            available.push(slot);
        }
    });
    return { occupied, available };
};

/**
 * Convierte una fecha del portafolio (formato `DD-MM-YYYY`) a `Date` local.
 * Devuelve `null` si el valor es vacío o no parseable.
 */
export const parsePortfolioDate = (value: string | null | undefined): Date | null => {
    if (!value) return null;
    const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value.trim());
    if (!match) return null;
    const [, dd, mm, yyyy] = match;
    const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(date.getTime()) ? null : date;
};

/** Días restantes hasta `date_end` (negativo si ya venció); `null` si no hay fecha. */
export const daysUntilExpiration = (
    slot: PortfolioSlot,
    now: Date = new Date()
): number | null => {
    const end = parsePortfolioDate(slot.date_end);
    if (!end) return null;
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffMs = end.getTime() - startOfToday.getTime();
    return Math.round(diffMs / (1000 * 60 * 60 * 24));
};

/** `true` si el slot vence dentro de `EXPIRING_SOON_DAYS` días (y aún no venció). */
export const isExpiringSoon = (slot: PortfolioSlot, now: Date = new Date()): boolean => {
    const days = daysUntilExpiration(slot, now);
    return days != null && days >= 0 && days <= EXPIRING_SOON_DAYS;
};

/** Resumen de una categoría de slots: totales para la cabecera de la sección. */
export interface SlotCategorySummary {
    occupied: number;
    available: number;
    expiringSoon: number;
    total: number;
}

export const summarizeSlots = (
    slots: PortfolioSlot[],
    now: Date = new Date()
): SlotCategorySummary => {
    const { occupied, available } = partitionSlots(slots);
    const expiringSoon = slots.filter((slot) => isExpiringSoon(slot, now)).length;
    return {
        occupied: occupied.length,
        available: available.length,
        expiringSoon,
        total: slots.length,
    };
};

/**
 * Deriva el estado de visibilidad de una propiedad. El slot que la referencia
 * (`property_id`) es la fuente de verdad: si existe, define si es Prime o
 * Destacado. Si ningún slot la referencia, se usa la bandera `highlighted`
 * como respaldo (`asignada` cuando está marcada, `libre` en caso contrario).
 */
export const derivePropertyVisibility = (
    property: PortfolioProperty,
    slots: PortfolioSlot[]
): PropertyVisibility => {
    const slot = slots.find((s) => s.property_id === property.id_property);
    if (slot) return isPrimeSlot(slot) ? 'prime' : 'destacado';
    return property.highlighted === 1 ? 'asignada' : 'libre';
};

/**
 * Una propiedad está libre (seleccionable/arrastrable) cuando ningún slot la
 * tiene asignada y no está marcada como destacada.
 */
export const isFreeProperty = (
    property: PortfolioProperty,
    slots: PortfolioSlot[] = []
): boolean =>
    property.highlighted !== 1 && !slots.some((s) => s.property_id === property.id_property);

/** Etiqueta legible del badge de estado de una propiedad. */
export const VISIBILITY_LABEL: Record<PropertyVisibility, string> = {
    libre: 'Libre',
    prime: 'Asignada a Prime',
    destacado: 'Asignada a Destacado',
    asignada: 'Asignada',
};

const MONTHS_ABBR = [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic',
];

/** Formatea `DD-MM-YYYY` a `D Mmm YYYY` (ej. "11 Jul 2026"); `null` si no aplica. */
export const formatExpirationDate = (value: string | null | undefined): string | null => {
    const date = parsePortfolioDate(value);
    if (!date) return null;
    return `${date.getDate()} ${MONTHS_ABBR[date.getMonth()]} ${date.getFullYear()}`;
};

/** Etiqueta corta para "por vencer" / "vencido" / días restantes. */
export const expirationLabel = (slot: PortfolioSlot, now: Date = new Date()): string => {
    const days = daysUntilExpiration(slot, now);
    if (days == null) return 'Sin vigencia';
    if (days < 0) return 'Vencido';
    if (days === 0) return 'Vence hoy';
    return `${days}d`;
};

/** Nombre legible de una propiedad: calle + número, o "Propiedad {id}". */
export const propertyTitle = (property: PortfolioProperty): string => {
    const { street, external_num } = property.address_array ?? {};
    const line = [street, external_num].filter(Boolean).join(' ').trim();
    return line || `Propiedad ${property.id_property}`;
};

export const formatAddress = (
    address: PortfolioProperty['address_array'] | null
): string => {
    if (!address) return 'Sin dirección';
    const parts = [
        [address.street, address.external_num].filter(Boolean).join(' '),
        address.colony,
        address.city,
        address.state,
        address.zipcode,
    ].filter((part) => part != null && String(part).trim() !== '');
    return parts.length ? parts.join(', ') : 'Sin dirección';
};
